import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Crown, Users, Bell, Megaphone, User, LogIn } from 'lucide-react';

const LOGO_URL = (process.env.PUBLIC_URL || '') + '/logo.png';

const Header = ({ onLoginClick, isLoggedIn, user, onLogout, unreadCount = 0, notifications = [], formatRelativeTime, markNotificationRead, renderNotifIcon }) => {
  const [showNotifications, setShowNotifications] = useState(false);
  const [isLogoVideoHover, setIsLogoVideoHover] = useState(false);
  const [currentMode, setCurrentMode] = useState('personal');
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const getMode = () => localStorage.getItem('ui_mode') || 'personal';
    setCurrentMode(getMode());

    const handleStorageChange = (e) => {
      if (e.key === 'ui_mode') {
        setCurrentMode(e.newValue || 'personal');
      }
    };

    const handleModeUpdate = () => {
      setCurrentMode(getMode());
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('ui_mode_updated', handleModeUpdate);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('ui_mode_updated', handleModeUpdate);
    };
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setShowNotifications(false);
      }
    };
    if (showNotifications) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showNotifications]);

  useEffect(() => {
    const v = document.getElementById('logo-video');
    if (!v) return;
    if (isLogoVideoHover) {
      v.currentTime = 0;
      const tryPlay = () => {
        v.play().catch(() => {
          setTimeout(tryPlay, 200);
        });
      };
      tryPlay();
      const ensurePlaying = setInterval(() => {
        if (isLogoVideoHover && v.paused) {
          v.play().catch(() => {});
        }
      }, 500);
      return () => clearInterval(ensurePlaying);
    } else {
      v.pause();
    }
  }, [isLogoVideoHover]);

  useEffect(() => {
    const v = document.getElementById('logo-video');
    if (!v) return;
    const handleVisibility = () => {
      if (isLogoVideoHover && v.paused && !document.hidden) {
        v.play().catch(() => {});
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);
    window.addEventListener('scroll', handleVisibility, { passive: true });
    return () => {
      document.removeEventListener('visibilitychange', handleVisibility);
      window.removeEventListener('scroll', handleVisibility);
    };
  }, [isLogoVideoHover]);

  return (
    <header className="fixed top-0 left-0 right-0 z-40">
      <div className="backdrop-blur-3xl bg-dark-bg/70 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3 md:gap-5" onMouseEnter={() => setIsLogoVideoHover(true)} onMouseLeave={() => { setIsLogoVideoHover(false); setTimeout(() => { const v = document.getElementById('logo-video'); if (v) { v.pause(); v.currentTime = 0; } }, 1000); }}>
              <div className="relative w-7 h-7 md:w-10 md:h-10 lg:w-14 lg:h-14 rounded-xl md:rounded-2xl overflow-hidden shadow-lg shadow-luxury-gold/20 group transition-all duration-300">
                <img src={LOGO_URL} alt="AI CREATOR" className={`w-full h-full rounded-xl md:rounded-2xl object-cover transition-opacity duration-1000 ease-in-out ${isLogoVideoHover ? 'opacity-0' : 'opacity-100'}`} />
                <video id="logo-video" src="/logo.mov" className={`absolute inset-0 w-full h-full rounded-xl md:rounded-2xl object-cover transition-opacity duration-1000 ease-in-out ${isLogoVideoHover ? 'opacity-100' : 'opacity-0 pointer-events-none'}`} autoPlay={false} muted loop playsInline preload="auto" disableRemotePlayback onContextMenu={(e) => e.preventDefault()} controlsList="nodownload nofullscreen noremoteplayback" style={{ willChange: 'transform', WebkitTransform: 'translateZ(0)' }} />
              </div>
              <div>
                <img src="/xiuqing.png" alt="秀卿官网" className="h-7 md:h-10 lg:h-14 transition-all duration-300" />
              </div>
            </Link>

            <nav className="hidden lg:flex items-center gap-1 xl:gap-2">
              {[
                { name: '创作流', path: '/work', newTab: true },
                { name: '模板库', path: currentMode === 'business' ? '/templates/business' : '/templates/personal' },
                { name: '工作台', path: '/content/enterprise' },
                { name: '云空间', path: '/cloud' },
                { name: '订阅服务', path: '/account' }
              ].map((item, i) => (
                item.newTab ? (
                  <a
                    key={item.name}
                    href={`/#${item.path}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => setShowMobileMenu(false)}
                    className={`px-3 md:px-4 lg:px-6 py-2 lg:py-3 rounded-lg lg:rounded-xl text-xs md:text-sm font-medium transition-all duration-400 relative group bg-transparent border-none cursor-pointer no-underline whitespace-nowrap ${
                      currentMode === 'personal'
                        ? 'text-transparent bg-clip-text bg-gradient-to-r from-orange-400 via-orange-300 to-white'
                        : 'text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 via-blue-300 to-white'
                    }`}
                    style={{
                      backgroundSize: '200% 200%',
                      animation: `${currentMode === 'personal' ? 'gradientShiftPersonal' : 'gradientShiftBusiness'} 3s ease infinite`,
                      animationDelay: `${i * 0.6}s`
                    }}
                  >
                    {item.name}
                    <div className={`absolute bottom-0.5 left-1/2 -translate-x-1/2 w-0 h-0.5 group-hover:w-3/4 transition-all duration-500 ${
                      currentMode === 'personal'
                        ? 'bg-gradient-to-r from-orange-400 to-amber-300'
                        : 'bg-gradient-to-r from-cyan-400 to-blue-300'
                  }`} />
                  </a>
                ) : (
                  <Link
                    key={item.name}
                    to={item.path}
                    onClick={() => setShowMobileMenu(false)}
                    className={`px-3 md:px-4 lg:px-6 py-2 lg:py-3 rounded-lg lg:rounded-xl text-xs md:text-sm font-medium transition-all duration-400 relative group bg-transparent border-none cursor-pointer no-underline whitespace-nowrap ${
                      currentMode === 'personal'
                        ? 'text-transparent bg-clip-text bg-gradient-to-r from-orange-400 via-orange-300 to-white'
                        : 'text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 via-blue-300 to-white'
                    }`}
                    style={{
                      backgroundSize: '200% 200%',
                      animation: `${currentMode === 'personal' ? 'gradientShiftPersonal' : 'gradientShiftBusiness'} 3s ease infinite`,
                      animationDelay: `${i * 0.6}s`
                    }}
                  >
                    {item.name}
                    <div className={`absolute bottom-0.5 left-1/2 -translate-x-1/2 w-0 h-0.5 group-hover:w-3/4 transition-all duration-500 ${
                      currentMode === 'personal'
                        ? 'bg-gradient-to-r from-orange-400 to-amber-300'
                        : 'bg-gradient-to-r from-cyan-400 to-blue-300'
                    }`} />
                  </Link>
                )
              ))}
            </nav>

            <button
              onClick={() => setShowMobileMenu(!showMobileMenu)}
              className="lg:hidden p-2 rounded-lg hover:bg-white/10 transition-colors"
            >
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                {showMobileMenu ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>

            {showMobileMenu && (
              <div className="lg:hidden absolute top-full left-0 right-0 mt-2 mx-4 p-4 bg-dark-bg/95 border border-white/10 rounded-2xl shadow-xl backdrop-blur-xl">
                <nav className="flex flex-col gap-2">
                  {[
                    { name: '创作流', path: '/work', newTab: true },
                    { name: '模板库', path: currentMode === 'business' ? '/templates/business' : '/templates/personal' },
                    { name: '工作台', path: '/content/enterprise' },
                    { name: '云空间', path: '/cloud' },
                    { name: '订阅服务', path: '/account' }
                  ].map((item, i) => (
                    item.newTab ? (
                      <a
                        key={item.name}
                        href={`/#${item.path}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() => setShowMobileMenu(false)}
                        className={`px-4 py-3 rounded-xl text-sm font-medium transition-all text-center ${
                          currentMode === 'personal'
                            ? 'bg-white/5 hover:bg-orange-400/20 text-orange-300 hover:text-orange-200'
                            : 'bg-white/5 hover:bg-cyan-400/20 text-cyan-300 hover:text-cyan-200'
                        }`}
                      >
                        {item.name}
                      </a>
                    ) : (
                      <Link
                        key={item.name}
                        to={item.path}
                        onClick={() => setShowMobileMenu(false)}
                        className={`px-4 py-3 rounded-xl text-sm font-medium transition-all text-center ${
                          currentMode === 'personal'
                            ? 'bg-white/5 hover:bg-orange-400/20 text-orange-300 hover:text-orange-200'
                            : 'bg-white/5 hover:bg-cyan-400/20 text-cyan-300 hover:text-cyan-200'
                        }`}
                      >
                        {item.name}
                      </Link>
                    )
                  ))}
                </nav>
              </div>
            )}

            <div className="flex items-center gap-2">
              {isLoggedIn ? (
                <>
                  <Link
                    to="/profile"
                    className="flex items-center gap-2 px-2 py-1 rounded-xl hover:bg-white/10 transition-all group"
                  >
                    <div className="relative">
                      {user?.avatar ? (
                        <div className="w-8 h-8 rounded-full ring-2 ring-luxury-gold/40 group-hover:ring-luxury-gold/70 transition-all overflow-hidden">
                          <img src={user.avatar.startsWith('http') ? user.avatar : window.location.origin + user.avatar} alt="头像" className="w-full h-full object-cover" />
                        </div>
                      ) : (
                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-luxury-gold to-amber-300 flex items-center justify-center ring-2 ring-luxury-gold/40">
                          <Users className="w-4 h-4 text-white" />
                        </div>
                      )}
                      {(() => {
                        const level = user?.vip_level ?? 0;
                        const badgeColors = { 1: 'from-[#F5A623] to-[#E8951A]', 2: 'from-[#1976D2] via-[#9C27B0] to-[#000000]' };
                        if (level === 0) return null;
                        return (
                          <div className={`absolute -top-0.5 -right-0.5 w-4 h-4 bg-gradient-to-r ${badgeColors[level] || 'from-[#BBDEFB] to-[#90CAF9]'} rounded-full flex items-center justify-center shadow-lg`}>
                            <Crown className="w-2.5 h-2.5 text-white" />
                          </div>
                        );
                      })()}
                    </div>
                    <span className="text-white font-medium hidden xl:inline text-sm">{user?.username || '用户'}</span>
                  </Link>
                  <div className="relative">
                    <button
                      onClick={() => setShowNotifications(!showNotifications)}
                      className="relative px-2 py-1.5 rounded-xl text-apple-gray hover:text-white hover:bg-white/10 transition-all flex items-center gap-0.5"
                    >
                      <Bell className="w-[18px] h-[18px]" />
                      {unreadCount > 0 && (
                        <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-[16px] px-0.5 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center animate-bounce">
                          {unreadCount > 99 ? '99+' : unreadCount}
                        </span>
                      )}
                    </button>
                    {showNotifications && (
                      <div ref={dropdownRef} className="absolute right-0 top-full mt-2 w-[400px] border border-white/20 rounded-2xl shadow-2xl overflow-hidden z-50" style={{ background: 'rgba(20,20,25,0.95)', backdropFilter: 'blur(20px)' }}>
                        <div className="p-4 border-b border-white/10 flex items-center justify-between">
                          <h3 className="font-bold text-white">通知中心</h3>
                          <Link to="/notifications" onClick={() => setShowNotifications(false)} className="text-xs text-luxury-gold hover:text-amber-300 transition-colors">
                            查看全部 →
                          </Link>
                        </div>
                        <div className="max-h-[380px] overflow-y-auto">
                          {(() => {
                            const systemNotifs = notifications.filter(n => n.source === 'system');
                            const userNotifs = notifications.filter(n => n.source === 'user');
                            return (
                              <>
                                {systemNotifs.length > 0 && (
                                  <div className="border-b border-white/10">
                                    <div className="px-4 py-2 bg-blue-500/5 flex items-center gap-2">
                                      <Megaphone className="w-3.5 h-3.5 text-blue-400" />
                                      <span className="text-[11px] font-semibold text-blue-400 uppercase tracking-wider">系统公告</span>
                                      <span className="text-[10px] text-apple-gray ml-auto">全站可见</span>
                                    </div>
                                    {systemNotifs.slice(0, 3).map((notif) => (
                              <Link
                                key={'s-' + notif.id}
                                to={`/notifications/${notif.id}?source=system`}
                                onClick={() => setShowNotifications(false)}
                                className={`block p-3.5 border-b border-white/5 hover:bg-white/5 transition-all cursor-pointer`}
                              >
                                <div className="flex gap-3">
                                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${notif.bg_color || 'bg-blue-500/10'} ${notif.color || 'text-blue-400'}`}>
                                    {renderNotifIcon ? renderNotifIcon(notif.icon, 'w-4 h-4') : <Bell className="w-4 h-4" />}
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <span className={`text-sm font-medium truncate block ${'text-white'}`}>{notif.title}</span>
                                    <p className="text-xs text-apple-gray line-clamp-1 mt-0.5">{notif.content}</p>
                                  </div>
                                </div>
                              </Link>
                                    ))}
                                  </div>
                                )}
                                {userNotifs.length > 0 && (
                                  <div>
                                    {systemNotifs.length > 0 && (
                                      <div className="px-4 py-2 bg-green-500/5 flex items-center gap-2 border-b border-white/5">
                                        <User className="w-3.5 h-3.5 text-green-400" />
                                        <span className="text-[11px] font-semibold text-green-400 uppercase tracking-wider">个人消息</span>
                                        <span className="text-[10px] text-red-400 ml-auto">{userNotifs.filter(n => !n.is_read && !n.read).length}条未读</span>
                                      </div>
                                    )}
                                    {userNotifs.slice(0, systemNotifs.length > 0 ? 3 : 5).map((notif) => (
                              <Link
                                key={'u-' + notif.id}
                                to={`/notifications/${notif.id}?source=user`}
                                onClick={() => { if (markNotificationRead) markNotificationRead(notif.id, 'user'); }}
                                className={`block p-3.5 border-b border-white/5 hover:bg-white/5 transition-all cursor-pointer ${!notif.is_read && !notif.read ? 'bg-luxury-gold/5' : ''}`}
                              >
                                <div className="flex gap-3">
                                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${notif.bg_color || 'bg-green-500/10'} ${notif.color || 'text-green-400'}`}>
                                    {renderNotifIcon ? renderNotifIcon(notif.icon, 'w-4 h-4') : <Bell className="w-4 h-4" />}
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2">
                                      {!notif.is_read && !notif.read && <span className="w-1.5 h-1.5 rounded-full bg-red-500 shrink-0" />}
                                      <span className={`text-sm font-medium truncate ${!notif.is_read && !notif.read ? 'text-white' : 'text-apple-gray'}`}>{notif.title}</span>
                                    </div>
                                    <p className="text-xs text-apple-gray line-clamp-1 mt-0.5">{notif.content}</p>
                                  </div>
                                </div>
                              </Link>
                                    ))}
                                  </div>
                                )}
                                {notifications.length === 0 && (
                                  <div className="p-8 text-center">
                                    <Bell className="w-10 h-10 mx-auto mb-3 text-apple-gray/30" />
                                    <p className="text-sm text-apple-gray">暂无通知</p>
                                  </div>
                                )}
                              </>
                            );
                          })()}
                        </div>
                      </div>
                    )}
                  </div>
                </>
              ) : (
                <button
                  onClick={onLoginClick}
                  className="px-6 py-3 rounded-xl font-semibold text-white bg-gradient-to-r from-luxury-gold via-amber-300 to-luxury-gold bg-[length:200%_100%] hover:bg-[length:100%_100%] transition-all duration-700 shadow-lg shadow-luxury-gold/20 hover:shadow-luxury-gold/40 flex items-center gap-2"
                >
                  <LogIn className="w-5 h-5" />
                  登录
                </button>
              )}
            </div>
          </div>
      </div>
    </div>
    </header>
  );
};

export { Header };
